// Read-only display of a field value: the same rendering in table cells, board cards and record properties.

import { FileLink, fileKeyOf } from './FileLink.tsx';
import { formatPlain, recordTitle, type FieldDef, type RecordObj, avatarUrl } from '@confdeck/core-shared';
import { useMeta, useLinked, useSync } from '../sync/react.tsx';
import { Avatar, CheckMark, Chip, RecordChip } from '../ui/primitives.tsx';
import { FitRow, type FitItem } from './FitRow.tsx';
import { QueryValueView } from './QueryField.tsx';
import s from './fields.module.css';

export function TermChips({ ids, wrap }: { ids: unknown; wrap?: boolean }) {
  const meta = useMeta();
  const list = (Array.isArray(ids) ? ids : ids ? [ids] : []) as string[];
  const items: FitItem[] = [];
  for (const id of list) {
    const t = meta.term(id);
    if (!t) continue;
    const tax = meta.taxonomy(t.taxonomyId);
    items.push({ key: id, label: t.name, node: <Chip color={t.color} dot={tax?.style === 'pipeline'} title={t.name}>{t.name}</Chip> });
  }
  return <FitRow items={items} wrap={wrap} />;
}

/** A person: avatar and name; several people — a row of them folded into «+N» like tags. */
export function UserValue({ id }: { id: unknown }) {
  const meta = useMeta();
  if (Array.isArray(id)) {
    const list = id.map((x) => meta.user(String(x))).filter((u) => !!u);
    if (list.length === 1) return <UserValue id={list[0]!.id} />;
    return <FitRow items={list.map((u) => ({ key: u.id, label: u.name, node: <span className={s.user} title={u.name}><Avatar name={u.name} color={u.color} src={avatarUrl(u)} size={18} /><span className={s.userName}>{u.name}</span></span> }))} />;
  }
  const u = typeof id === 'string' ? meta.user(id) : undefined;
  if (!u) return null;
  return <span className={s.user}><Avatar name={u.name} color={u.color} src={avatarUrl(u)} size={18} /><span className={s.userName}>{u.name}</span></span>;
}

/** Drawn in the row at most: a cell with hundreds of links measures the first ones; the badge counts the rest too. */
const MAX_LINKS = 40;

export function RelationValue({ rec, field, onOpen, wrap }: { rec: RecordObj; field: FieldDef; onOpen?: (id: string, e: React.MouseEvent) => void; wrap?: boolean }) {
  const { store } = useSync();
  const meta = useMeta();
  const ids = useLinked(rec, field.key);
  const target = meta.relationTarget(field);
  const icon = target ? meta.type(target.typeId)?.icon : undefined;
  if (!ids.length) return null;
  const item = (id: string): FitItem => {
    const title = recordTitle(store.record(id), meta);
    return { key: id, label: title, node: <RecordChip icon={icon} onClick={onOpen ? (e) => { e.stopPropagation(); onOpen(id, e); } : undefined}>{title}</RecordChip> };
  };
  // The popover lists every link, built only when it opens.
  return <FitRow items={ids.slice(0, MAX_LINKS).map(item)} total={ids.length} all={ids.length > MAX_LINKS ? () => ids.map(item) : undefined} wrap={wrap} />;
}

export function FieldValue({ rec, field, onOpenRecord, wrap }: { rec: RecordObj; field: FieldDef; onOpenRecord?: (id: string, e: React.MouseEvent) => void; wrap?: boolean }) {
  const meta = useMeta();
  const v = rec.data[field.key];
  switch (field.fieldType) {
    case 'relation': return <RelationValue rec={rec} field={field} onOpen={onOpenRecord} wrap={wrap} />;
    case 'taxonomy': return <TermChips ids={v} wrap={wrap} />;
    case 'user': return <UserValue id={v} />;
    case 'checkbox': return v ? <CheckMark checked /> : null;
    case 'money': case 'number': return <span className="num">{formatPlain(field, v, meta)}</span>;
    case 'email': return v ? <span className={s.link}>{String(v)}</span> : null;
    case 'url': { const key = fileKeyOf(v); return key ? <FileLink fileKey={key} /> : v ? <span className={s.link}>{String(v).replace(/^https?:\/\//, '')}</span> : null; }
    case 'longtext': return <span className={s.clamp}>{String(v ?? '')}</span>;
    case 'query': return <QueryValueView rec={rec} field={field} wrap={wrap} />;
    default: return <>{formatPlain(field, v, meta)}</>;
  }
}
